import React, { useEffect, useState, useCallback } from "react";
import API from "../../utils/api";
import { useAuth } from "../../App";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripExpenses, setTripExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  // Advance requests state
  const [advanceRequests, setAdvanceRequests] = useState([]);
  const [loadingAdvances, setLoadingAdvances] = useState(true);
  const [declineMode, setDeclineMode] = useState(null); // request id being declined
  const [declineNote, setDeclineNote] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [actionMsg, setActionMsg] = useState(null); // { type: "success"|"error", text }

  // Cash bill approval requests state
  const [cashBillApprovals, setCashBillApprovals] = useState([]);
  const [loadingCashBills, setLoadingCashBills] = useState(true);
  const [cashDeclineMode, setCashDeclineMode] = useState(null);
  const [cashDeclineNote, setCashDeclineNote] = useState("");
  const [cashProcessingId, setCashProcessingId] = useState(null);

  const company = process.env.REACT_APP_COMPANY || "ebek";
  const accentColor = company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  const loadTrips = useCallback(() => {
    API.get("/trips/team")
      .then(r => { setTrips(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const loadAdvances = useCallback(() => {
    setLoadingAdvances(true);
    API.get("/advance-requests/pending")
      .then(r => { setAdvanceRequests(r.data || []); })
      .catch(() => setAdvanceRequests([]))
      .finally(() => setLoadingAdvances(false));
  }, []);

  const loadCashBillApprovals = useCallback(() => {
    setLoadingCashBills(true);
    API.get("/expenses/cash-bill-approvals/pending")
      .then(r => { setCashBillApprovals(r.data || []); })
      .catch(() => setCashBillApprovals([]))
      .finally(() => setLoadingCashBills(false));
  }, []);

  useEffect(() => {
    loadTrips();
    loadAdvances();
    loadCashBillApprovals();
  }, [loadTrips, loadAdvances, loadCashBillApprovals]);

  const handleApprove = async (req) => {
    setProcessingId(req.id);
    try {
      await API.post(`/advance-requests/${req.id}/approve`, { manager_notes: "" });
      setActionMsg({ type: "success", text: `✅ Advance of ₹${parseFloat(req.amount).toLocaleString("en-IN")} approved for ${req.employee_name}. Finance team has been notified.` });
      setAdvanceRequests(prev => prev.filter(r => r.id !== req.id));
    } catch (err) {
      setActionMsg({ type: "error", text: "❌ Failed to approve. Please try again." });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (req) => {
    setProcessingId(req.id);
    try {
      await API.post(`/advance-requests/${req.id}/decline`, { manager_notes: declineNote });
      setActionMsg({ type: "success", text: `Advance request from ${req.employee_name} has been declined.` });
      setAdvanceRequests(prev => prev.filter(r => r.id !== req.id));
      setDeclineMode(null);
      setDeclineNote("");
    } catch (err) {
      setActionMsg({ type: "error", text: "❌ Failed to decline. Please try again." });
    } finally {
      setProcessingId(null);
    }
  };

  const handleCashApprove = async (req) => {
    setCashProcessingId(req.id);
    try {
      await API.post(`/expenses/cash-bill-approvals/${req.id}/approve`);
      setActionMsg({ type: "success", text: `✅ Cash bill of ₹${parseFloat(req.amount).toLocaleString("en-IN")} approved for ${req.employee_name}. Finance team will review next.` });
      setCashBillApprovals(prev => prev.filter(r => r.id !== req.id));
    } catch (err) {
      setActionMsg({ type: "error", text: "❌ Failed to approve. Please try again." });
    } finally { setCashProcessingId(null); }
  };

  const handleCashDecline = async (req) => {
    setCashProcessingId(req.id);
    try {
      await API.post(`/expenses/cash-bill-approvals/${req.id}/decline`, { notes: cashDeclineNote });
      setActionMsg({ type: "success", text: `Cash bill from ${req.employee_name} has been declined.` });
      setCashBillApprovals(prev => prev.filter(r => r.id !== req.id));
      setCashDeclineMode(null);
      setCashDeclineNote("");
    } catch (err) {
      setActionMsg({ type: "error", text: "❌ Failed to decline. Please try again." });
    } finally { setCashProcessingId(null); }
  };

  const hoursAgo = (dateStr) => {
    if (!dateStr) return null;
    const diff = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return "just now";
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h ago`;
  };

  const fmtDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  const fmtHours = (hours) => {
    if (hours == null) return null;
    const h = Math.round(hours);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    const rem = h % 24;
    return rem > 0 ? `${d}d ${rem}h` : `${d}d`;
  };

  const viewTrip = async (trip) => {
    setSelectedTrip(trip);
    setLoadingExpenses(true);
    try {
      const r = await API.get(`/expenses/trip/${trip.id}`);
      setTripExpenses(r.data);
    } catch { setTripExpenses([]); }
    finally { setLoadingExpenses(false); }
  };

  const viewBill = async (expenseId) => {
    try {
      const response = await API.get(`/expenses/${expenseId}/bill-file`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setActionMsg({ type:"error", text:"Could not open bill." }); }
  };

  const submittedTrips = trips.filter(t => t.status === "submitted");
  const activeTrips   = trips.filter(t => t.status === "active");
  const totalPending  = submittedTrips.reduce((s, t) => s + parseFloat(t.total_expenses || 0), 0);

  const CATEGORY_ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌" };

  const StatCard = ({ label, value, color, icon, border, sub }) => {
    const valStr = String(value);
    const fontSize = valStr.length > 13 ? 13 : valStr.length > 10 ? 15 : valStr.length > 7 ? 18 : 22;
    return (
    <div className="stat-card" style={{ borderLeftColor: border || color }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <span style={{ fontSize:12, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
        <span style={{ fontSize:22 }}>{icon}</span>
      </div>
      <div style={{ fontSize, fontWeight:800, color: color || "#1f2937", lineHeight:1.2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:"#9ca3af", marginTop:4 }}>{sub}</div>}
    </div>
    );
  };

  // ── Trip Detail View ────────────────────────────────────────────────────────
  if (selectedTrip) {
    const balance = parseFloat(selectedTrip.advance_amount||0) - parseFloat(selectedTrip.total_expenses||0);
    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:24 }}>
          <button onClick={() => setSelectedTrip(null)} className="btn btn-secondary btn-sm">← Back</button>
          <div style={{ flex:1 }}>
            <h1 className="page-title">{selectedTrip.title}</h1>
            <p className="page-subtitle">
              {selectedTrip.employee_name && <span>👤 {selectedTrip.employee_name} &nbsp;•&nbsp; </span>}
              {selectedTrip.purpose}
              {selectedTrip.destination && ` • ${selectedTrip.destination}`}
            </p>
          </div>
          <span className={`badge badge-${selectedTrip.status}`} style={{ fontSize:13, padding:"6px 14px" }}>{selectedTrip.status}</span>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:16, marginBottom:24 }}>
          {[
            { label:"Advance", value:`₹${parseFloat(selectedTrip.advance_amount||0).toLocaleString("en-IN")}`, color:"#d97706", bg:"#FEF3C7", icon:"💰" },
            { label:"Total Spent", value:`₹${parseFloat(selectedTrip.total_expenses||0).toLocaleString("en-IN")}`, color:"#E31E24", bg:"#FEE2E2", icon:"💸" },
            { label:"Balance", value:`${balance>=0?"+":""}₹${Math.abs(balance).toLocaleString("en-IN")}`, color:balance>=0?"#059669":"#DC2626", bg:balance>=0?"#D1FAE5":"#FEE2E2", icon:balance>=0?"✅":"⚠️" },
            { label:"Expenses", value:tripExpenses.length, color:"#4f46e5", bg:"#EEF2FF", icon:"📋" },
          ].map(s => (
            <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"16px 20px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:s.color, opacity:0.7, textTransform:"uppercase" }}>{s.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:s.color, marginTop:4 }}>{s.icon} {s.value}</div>
            </div>
          ))}
        </div>

        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:"20px 24px", borderBottom:"1px solid #f3f4f6" }}>
            <h3 style={{ fontSize:17, fontWeight:700 }}>Expense Entries</h3>
          </div>
          {loadingExpenses ? (
            <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>Loading...</div>
          ) : tripExpenses.length === 0 ? (
            <div className="empty-state"><h3>No expenses found</h3></div>
          ) : (
            <div>
              {tripExpenses.map(exp => (
                <div key={exp.id} style={{ padding:"16px 24px", borderBottom:"1px solid #f9fafb", display:"flex", alignItems:"center", gap:16 }}>
                  <div style={{ fontSize:26 }}>{CATEGORY_ICONS[exp.category] || "📌"}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontWeight:700, color:"#1f2937" }}>{exp.category}</span>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                        background: exp.bill_type==="cash_voucher" ? "#FEF3C7" : "#D1FAE5",
                        color: exp.bill_type==="cash_voucher" ? "#D97706" : "#065F46", fontWeight:600 }}>
                        {exp.bill_type === "cash_voucher" ? "Cash Voucher" : "Bill"}
                      </span>
                    </div>
                    <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>{exp.description}</div>
                    <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>
                      📅 {new Date(exp.expense_date).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:17, fontWeight:800, color:"#E31E24" }}>
                      ₹{parseFloat(exp.amount).toLocaleString("en-IN")}
                    </div>
                    {exp.bill_url && (
                      <button type="button" onClick={() => viewBill(exp.id)}
                        style={{ border:0, background:"transparent", padding:0, cursor:"pointer", fontSize:12, color:"#2AB5A5", fontWeight:600 }}>📎 View Bill</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main Dashboard ──────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header Banner */}
      <div className="page-banner" style={{
        background:"linear-gradient(135deg,#1D4ED8 0%,#1e40af 100%)",
        borderRadius:16, padding:"28px 32px", marginBottom:28, color:"white",
        display:"flex", alignItems:"center", justifyContent:"space-between"
      }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, marginBottom:6 }}>
            Welcome, {user?.name?.split(" ")[0]}! 👋
          </div>
          <div style={{ fontSize:14, opacity:0.85 }}>
            Review team trips and manage advance requests.
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:12, opacity:0.7, marginBottom:4 }}>Today</div>
          <div style={{ fontSize:15, fontWeight:600 }}>
            {new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long" })}
          </div>
        </div>
      </div>

      {/* Action message toast */}
      {actionMsg && (
        <div style={{
          marginBottom:20, padding:"14px 20px", borderRadius:12, fontWeight:600, fontSize:14,
          background: actionMsg.type === "success" ? "#D1FAE5" : "#FEE2E2",
          color: actionMsg.type === "success" ? "#065F46" : "#991B1B",
          border: `1px solid ${actionMsg.type === "success" ? "#6EE7B7" : "#FECACA"}`,
          display:"flex", alignItems:"center", justifyContent:"space-between"
        }}>
          <span>{actionMsg.text}</span>
          <button onClick={() => setActionMsg(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"inherit", opacity:0.6 }}>×</button>
        </div>
      )}

      {/* ── Pending Advance Requests ── */}
      {(loadingAdvances || advanceRequests.length > 0) && (
        <div className="card" style={{ marginBottom:24, border:"2px solid #FCD34D", background:"#FFFBEB" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: advanceRequests.length > 0 ? 16 : 0 }}>
            <span style={{ fontSize:22 }}>💰</span>
            <div style={{ flex:1 }}>
              <h3 style={{ fontSize:16, fontWeight:700, color:"#92400e", margin:0 }}>
                Advance Requests Pending Approval
                {advanceRequests.length > 0 && (
                  <span style={{ marginLeft:8, background:"#F59E0B", color:"white", borderRadius:20, padding:"2px 10px", fontSize:13 }}>
                    {advanceRequests.length}
                  </span>
                )}
              </h3>
              <p style={{ fontSize:12, color:"#b45309", margin:0, marginTop:2 }}>
                Your team members are waiting for your approval
              </p>
            </div>
          </div>

          {loadingAdvances ? (
            <div style={{ textAlign:"center", padding:"20px 0", color:"#9ca3af" }}>Loading...</div>
          ) : advanceRequests.length === 0 ? (
            <div style={{ textAlign:"center", padding:"16px 0", color:"#6b7280", fontSize:14 }}>
              ✅ No pending advance requests
            </div>
          ) : (
            <div style={{ display:"grid", gap:14 }}>
              {advanceRequests.map(req => {
                const waiting = hoursAgo(req.requested_at);
                const isUrgent = req.hours_pending && req.hours_pending > 24;
                const isDeclineOpen = declineMode === req.id;
                const isProcessing = processingId === req.id;

                return (
                  <div key={req.id} style={{
                    background:"white", borderRadius:12, padding:"18px 20px",
                    border: isUrgent ? "2px solid #FCA5A5" : "1px solid #FDE68A",
                    boxShadow:"0 1px 4px rgba(0,0,0,0.06)"
                  }}>
                    {/* Top row: employee info + amount + wait time */}
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:12 }}>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span style={{ fontSize:15, fontWeight:800, color:"#1f2937" }}>
                            {req.employee_name}
                          </span>
                          {isUrgent ? (
                            <span style={{ fontSize:11, background:"#FEE2E2", color:"#DC2626", borderRadius:8, padding:"2px 8px", fontWeight:700 }}>
                              ⚠️ Waiting {fmtHours(req.hours_pending)}
                            </span>
                          ) : (
                            <span style={{ fontSize:11, background:"#FEF3C7", color:"#92400e", borderRadius:8, padding:"2px 8px", fontWeight:600 }}>
                              🕐 {waiting}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:12, color:"#9ca3af", marginTop:3 }}>
                          {req.employee_email}
                        </div>
                        {/* Requested timestamp */}
                        <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>
                          📅 Requested: <strong>{fmtDate(req.requested_at)}</strong>
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Amount Requested</div>
                        <div style={{ fontSize:22, fontWeight:800, color:accentColor }}>
                          ₹{parseFloat(req.amount).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>

                    {/* Trip details row */}
                    <div style={{
                      background:"#F9FAFB", borderRadius:8, padding:"10px 14px", marginBottom:14,
                      display:"flex", flexWrap:"wrap", gap:16
                    }}>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Trip</div>
                        <div style={{ fontSize:14, fontWeight:700, color:"#1f2937", marginTop:1 }}>{req.trip_title || "—"}</div>
                      </div>
                      {req.destination && (
                        <div>
                          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Destination</div>
                          <div style={{ fontSize:14, color:"#374151", marginTop:1 }}>📍 {req.destination}</div>
                        </div>
                      )}
                      {req.start_date && (
                        <div>
                          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Travel Dates</div>
                          <div style={{ fontSize:13, color:"#374151", marginTop:1 }}>
                            📅 {new Date(req.start_date).toLocaleDateString("en-IN")}
                            {req.end_date && ` → ${new Date(req.end_date).toLocaleDateString("en-IN")}`}
                          </div>
                        </div>
                      )}
                      {req.purpose && (
                        <div>
                          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Purpose</div>
                          <div style={{ fontSize:13, color:"#374151", marginTop:1 }}>{req.purpose}</div>
                        </div>
                      )}
                    </div>

                    {/* Notes from employee */}
                    {req.notes && (
                      <div style={{ background:"#EFF6FF", borderRadius:8, padding:"8px 12px", marginBottom:14, fontSize:13, color:"#1e40af" }}>
                        💬 <em>"{req.notes}"</em>
                      </div>
                    )}

                    {/* Decline notes input */}
                    {isDeclineOpen && (
                      <div style={{ marginBottom:12 }}>
                        <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>
                          Reason for declining <span style={{ color:"#9ca3af", fontWeight:400 }}>(optional — employee will see this)</span>
                        </label>
                        <textarea
                          value={declineNote}
                          onChange={e => setDeclineNote(e.target.value)}
                          placeholder="e.g. Budget not available this month. Please resubmit next quarter."
                          rows={2}
                          style={{
                            width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #E5E7EB",
                            fontSize:13, resize:"vertical", fontFamily:"inherit", boxSizing:"border-box"
                          }}
                        />
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                      {!isDeclineOpen ? (
                        <>
                          <button
                            disabled={isProcessing}
                            onClick={() => handleApprove(req)}
                            style={{
                              background: isProcessing ? "#9ca3af" : "#059669",
                              color:"white", border:"none", borderRadius:8,
                              padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer",
                              display:"flex", alignItems:"center", gap:6
                            }}
                          >
                            {isProcessing ? "Processing..." : "✅ Approve"}
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => { setDeclineMode(req.id); setDeclineNote(""); }}
                            style={{
                              background:"white", color:"#DC2626", border:"2px solid #DC2626",
                              borderRadius:8, padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}
                          >
                            ❌ Decline
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            disabled={isProcessing}
                            onClick={() => handleDecline(req)}
                            style={{
                              background: isProcessing ? "#9ca3af" : "#DC2626",
                              color:"white", border:"none", borderRadius:8,
                              padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}
                          >
                            {isProcessing ? "Processing..." : "Confirm Decline"}
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => { setDeclineMode(null); setDeclineNote(""); }}
                            style={{
                              background:"white", color:"#6b7280", border:"1px solid #D1D5DB",
                              borderRadius:8, padding:"10px 18px", fontSize:14, fontWeight:600,
                              cursor:"pointer"
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Cash Voucher Approval Requests ── */}
      {(loadingCashBills || cashBillApprovals.length > 0) && (
        <div className="card" style={{ marginBottom:24, border:"2px solid #FCA5A5", background:"#FFF5F5" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: cashBillApprovals.length > 0 ? 16 : 0 }}>
            <span style={{ fontSize:22 }}>🧾</span>
            <div style={{ flex:1 }}>
              <h3 style={{ fontSize:16, fontWeight:700, color:"#991B1B", margin:0 }}>
                Cash Voucher Approval Requests
                {cashBillApprovals.length > 0 && (
                  <span style={{ marginLeft:8, background:"#DC2626", color:"white", borderRadius:20, padding:"2px 10px", fontSize:13 }}>
                    {cashBillApprovals.length}
                  </span>
                )}
              </h3>
              <p style={{ fontSize:12, color:"#b91c1c", margin:0, marginTop:2 }}>
                Team members need your approval when their cash vouchers for a single day add up to more than ₹500
              </p>
            </div>
          </div>

          {loadingCashBills ? (
            <div style={{ textAlign:"center", padding:"20px 0", color:"#9ca3af" }}>Loading...</div>
          ) : cashBillApprovals.length === 0 ? (
            <div style={{ textAlign:"center", padding:"16px 0", color:"#6b7280", fontSize:14 }}>
              ✅ No pending cash voucher approvals
            </div>
          ) : (
            <div style={{ display:"grid", gap:14 }}>
              {cashBillApprovals.map(req => {
                const isDeclineOpen = cashDeclineMode === req.id;
                const isProcessing = cashProcessingId === req.id;
                return (
                  <div key={req.id} style={{
                    background:"white", borderRadius:12, padding:"18px 20px",
                    border:"1px solid #FECACA", boxShadow:"0 1px 4px rgba(0,0,0,0.06)"
                  }}>
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:12 }}>
                      <div>
                        <div style={{ fontSize:15, fontWeight:800, color:"#1f2937" }}>{req.employee_name}</div>
                        <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>{req.employee_email}</div>
                        <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>
                          📅 Requested: <strong>{new Date(req.created_at).toLocaleString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}</strong>
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Cash Voucher Amount</div>
                        <div style={{ fontSize:22, fontWeight:800, color:"#DC2626" }}>
                          ₹{parseFloat(req.amount).toLocaleString("en-IN")}
                        </div>
                        {/* The cap is on the day as a whole, so show what the
                            employee's cash vouchers for that date come to. */}
                        {req.cash_voucher_day_total != null && (
                          <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>
                            ₹{parseFloat(req.cash_voucher_day_total).toLocaleString("en-IN")} total for {new Date(req.expense_date).toLocaleDateString("en-IN")}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expense details */}
                    <div style={{ background:"#F9FAFB", borderRadius:8, padding:"10px 14px", marginBottom:14, display:"flex", flexWrap:"wrap", gap:16 }}>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Trip</div>
                        <div style={{ fontSize:14, fontWeight:700, color:"#1f2937", marginTop:1 }}>{req.trip_title}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Category</div>
                        <div style={{ fontSize:14, color:"#374151", marginTop:1 }}>{req.category}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Description</div>
                        <div style={{ fontSize:13, color:"#374151", marginTop:1 }}>{req.description}</div>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, textTransform:"uppercase" }}>Expense Date</div>
                        <div style={{ fontSize:13, color:"#374151", marginTop:1 }}>
                          {new Date(req.expense_date).toLocaleDateString("en-IN")}
                        </div>
                      </div>
                    </div>

                    {/* Decline note input */}
                    {isDeclineOpen && (
                      <div style={{ marginBottom:12 }}>
                        <label style={{ fontSize:13, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>
                          Reason for declining <span style={{ color:"#9ca3af", fontWeight:400 }}>(optional)</span>
                        </label>
                        <textarea
                          value={cashDeclineNote}
                          onChange={e => setCashDeclineNote(e.target.value)}
                          placeholder="e.g. Please get a proper receipt instead."
                          rows={2}
                          style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #E5E7EB", fontSize:13, resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" }}
                        />
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                      {Number(req.has_bill) ? (
                        <button type="button" onClick={() => viewBill(req.expense_id)}
                          style={{ background:"white", color:"#0f766e", border:"2px solid #0d9488", borderRadius:8, padding:"10px 22px", fontSize:14, fontWeight:700, cursor:"pointer" }}>
                          📎 View Bill
                        </button>
                      ) : (
                        <span style={{ fontSize:12, color:"#9ca3af", alignSelf:"center" }}>No bill attached</span>
                      )}
                      {!isDeclineOpen ? (
                        <>
                          <button
                            disabled={isProcessing}
                            onClick={() => handleCashApprove(req)}
                            style={{
                              background: isProcessing ? "#9ca3af" : "#059669",
                              color:"white", border:"none", borderRadius:8,
                              padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}>
                            {isProcessing ? "Processing..." : "✅ Approve"}
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => { setCashDeclineMode(req.id); setCashDeclineNote(""); }}
                            style={{
                              background:"white", color:"#DC2626", border:"2px solid #DC2626",
                              borderRadius:8, padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}>
                            ❌ Decline
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            disabled={isProcessing}
                            onClick={() => handleCashDecline(req)}
                            style={{
                              background: isProcessing ? "#9ca3af" : "#DC2626",
                              color:"white", border:"none", borderRadius:8,
                              padding:"10px 22px", fontSize:14, fontWeight:700,
                              cursor: isProcessing ? "not-allowed" : "pointer"
                            }}>
                            {isProcessing ? "Processing..." : "Confirm Decline"}
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => { setCashDeclineMode(null); setCashDeclineNote(""); }}
                            style={{
                              background:"white", color:"#6b7280", border:"1px solid #D1D5DB",
                              borderRadius:8, padding:"10px 18px", fontSize:14, fontWeight:600, cursor:"pointer"
                            }}>
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:28 }}>
        <StatCard label="Total Team Trips" value={trips.length} icon="✈️" border="#6366f1" color="#4f46e5" />
        <StatCard label="Active Trips" value={activeTrips.length} icon="🟢" border="#10b981" color="#059669" />
        <StatCard label="Awaiting Review" value={submittedTrips.length} icon="🕐" border="#f59e0b" color="#d97706" sub={submittedTrips.length > 0 ? "Action needed" : "All clear"} />
        <StatCard label="Pending Amount" value={`₹${totalPending.toLocaleString("en-IN")}`} icon="💰" border="#E31E24" color="#E31E24" sub="Submitted, unsettled" />
        <StatCard label="Advance Requests" value={advanceRequests.length} icon="🏦" border="#f59e0b" color="#d97706" sub={advanceRequests.length > 0 ? "Needs approval" : "All clear"} />
        <StatCard label="Cash Voucher Approvals" value={cashBillApprovals.length} icon="🧾" border="#DC2626" color="#DC2626" sub={cashBillApprovals.length > 0 ? "Needs approval" : "All clear"} />
      </div>

      {/* Submitted Trips needing attention */}
      {submittedTrips.length > 0 && (
        <div className="card" style={{ marginBottom:24, border:"2px solid #FEF3C7" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <span style={{ fontSize:20 }}>🕐</span>
            <h3 style={{ fontSize:16, fontWeight:700, color:"#92400e" }}>Awaiting Finance Review ({submittedTrips.length})</h3>
          </div>
          <div style={{ display:"grid", gap:12 }}>
            {submittedTrips.map(trip => {
              const bal = parseFloat(trip.advance_amount||0) - parseFloat(trip.total_expenses||0);
              return (
                <div key={trip.id} style={{ background:"#FFFBEB", borderRadius:10, padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700, color:"#1f2937", fontSize:15 }}>{trip.title}</div>
                    <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>
                      👤 {trip.employee_name || "—"} &nbsp;•&nbsp; 📍 {trip.destination || "—"}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:20, alignItems:"center" }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>SPENT</div>
                      <div style={{ fontSize:16, fontWeight:800, color:"#E31E24" }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>BALANCE</div>
                      <div style={{ fontSize:16, fontWeight:800, color: bal>=0?"#059669":"#DC2626" }}>
                        {bal>=0?"+":""}₹{Math.abs(bal).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => viewTrip(trip)}>View Expenses →</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Trips Table */}
      <div className="card">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <h3 style={{ fontSize:17, fontWeight:700, color:"#1f2937" }}>All Team Trips</h3>
            <p style={{ fontSize:13, color:"#9ca3af", marginTop:2 }}>Complete history of your team's travel</p>
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading trips...</div>
        ) : trips.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize:48 }}>✈️</div>
            <h3>No team trips yet</h3>
            <p>Trips created by your team members will appear here</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Trip</th>
                  <th>Destination</th>
                  <th>Dates</th>
                  <th>Advance</th>
                  <th>Spent</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {trips.map(trip => {
                  const balance = parseFloat(trip.advance_amount||0) - parseFloat(trip.total_expenses||0);
                  return (
                    <tr key={trip.id}>
                      <td>
                        <div style={{ fontWeight:600, color:"#1f2937" }}>{trip.employee_name || "—"}</div>
                        <div style={{ fontSize:12, color:"#9ca3af" }}>{trip.employee_email}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight:600, color:"#1f2937" }}>{trip.title}</div>
                        <div style={{ fontSize:12, color:"#9ca3af" }}>{trip.purpose}</div>
                      </td>
                      <td style={{ color:"#6b7280" }}>{trip.destination || "—"}</td>
                      <td style={{ fontSize:13, color:"#6b7280" }}>
                        {new Date(trip.start_date).toLocaleDateString("en-IN")}
                        {trip.end_date && <><br/>{new Date(trip.end_date).toLocaleDateString("en-IN")}</>}
                      </td>
                      <td style={{ fontWeight:600 }}>₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}</td>
                      <td style={{ fontWeight:600, color:"#E31E24" }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</td>
                      <td style={{ fontWeight:700, color: balance>=0?"#059669":"#DC2626" }}>
                        {balance>=0?"+":""}₹{Math.abs(balance).toLocaleString("en-IN")}
                      </td>
                      <td><span className={`badge badge-${trip.status}`}>{trip.status}</span></td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => viewTrip(trip)}>View →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
